import { observer } from 'mobx-react';
import { FC, ReactElement, useCallback } from 'react';
import { localeStore } from '~/mobx/localesStore';
import { modalStore } from '~/mobx/modalStore';
import askAgain, { AskAgainKey } from '~/services/askAgain';
import { BRANCHES_MODAL_FETCH_PARAMETER, IBranch, UNAVAILABLE_BRANCHES_FLAG } from '~/types/branches';
import { arrayCompare, getTranslate } from '~/utils/helpers';
import { IAskAgainModalSubmit } from '~/view/containers/Modals/AskAgainModal/AskAgainModal';
import { BranchesModalProfile, IBranchesModalItem } from '~/view/containers/Modals/BranchesModal/BranchesModal';
import ProfileBranches from '~/view/containers/ProfileBranches/ProfileBranches';
import { dispatchingRulesStore } from '~/view/screens/DeliveryManagementScreen/screens/DispatchingRulesScreen/stores/dispatchingRulesStore';

export const DispatchingRulesBranchesList: FC = observer((): ReactElement => {
  const updateBranchesList = useCallback(
    (branches: IBranchesModalItem[]) => {
      if (!arrayCompare(branches, dispatchingRulesStore.selectedProfile?.branches || [])) {
        dispatchingRulesStore.movingBranches(branches);
        modalStore.closeBranchesModal();
      }
    },
    [dispatchingRulesStore.selectedProfile?.branches]
  );

  const askAgainChecker = useCallback(
    (branch: IBranch | undefined): boolean => {
      if (!branch || !branch.profile_name) return false;
      return branch.profile_name !== dispatchingRulesStore.selectedProfile?.name;
    },
    [dispatchingRulesStore.selectedProfile?.name]
  );

  const askAgainHandler = (branch: IBranch | undefined, callback: (branch: IBranch, data: IAskAgainModalSubmit) => void) => {
    if (!branch || !branch.profile_name) return true;

    askAgain.showModal(
      {
        submit: (data) => askAgainSubmit(data, branch, callback),
        message: `${localeStore.t('are_you_sure_want_to_change_profile')} ${getTranslate(branch.name)} ${localeStore.t('txt_from')} ${
          branch.profile_name
        } ${localeStore.t('txt_select_to')} ${dispatchingRulesStore.selectedProfile?.name || ''} ?`,
      },
      AskAgainKey.DELIVERY_PROFILE
    );
  };

  const askAgainSubmit = (data: IAskAgainModalSubmit, branch: IBranch, callback: (branch: IBranch, data: IAskAgainModalSubmit) => void) => {
    askAgain.hideModal(AskAgainKey.DELIVERY_PROFILE, data.checkBoxStatus);
    callback(branch, data);
  };

  const openBranchesModal = useCallback(() => {
    modalStore.openBranchesModal({
      selectedBranches: dispatchingRulesStore.selectedProfile?.branches || [],
      onSave: updateBranchesList,
      askAgainChecker,
      askAgainHandler,
      headerTitle: localeStore.t('adding_branches_to_profile_for_fast'),
      isMulti: true,
      saveWithoutBranches: true,
      fetchParamStatus: BRANCHES_MODAL_FETCH_PARAMETER.ALL,
      fetchParamDelete: BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE,
      checkUnavailable: true,
      unavailableFlag: UNAVAILABLE_BRANCHES_FLAG.DELETE,
      highlightUnavailable: true,
      profileNamesToShow: [BranchesModalProfile.DELIVERY],
    });
  }, [dispatchingRulesStore.selectedProfile?.branches]);

  return (
    <ProfileBranches
      selectedProfile={dispatchingRulesStore.selectedProfile || undefined}
      onDeleteProfile={dispatchingRulesStore.deleteProfile}
      onDeleteBranch={dispatchingRulesStore.deleteBranch}
      onBranchesModalOpen={openBranchesModal}
      loading={dispatchingRulesStore.loading}
      availableDragAndDrop
    />
  );
});
